import {get, isNaN, map, maxBy} from "lodash";
import {ITriviaLadder as ILadder} from "modules/reducers/trivia_ladders";
import {getLadder, getPodiumSelector} from "modules/selectors/trivia_ladder";
import {EnumOrders, EnumTriviaLadders} from "modules/utils/enums";
import {useCallback, useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";


export const useTableHook = (ladder_type: EnumTriviaLadders) => {

	const {ladders} = useSelector(getLadder);

	const ladder = ladders[ ladder_type ].items;
	const next = ladders[ ladder_type ].next;
	const is_fetched = ladders[ ladder_type ].is_fetched;
	const first_ladder_for_placeholder = ladder[ 0 ];

	return {
		first_ladder_for_placeholder,
		ladder,
		next,
		is_fetched
	};
};

export interface IPodium extends ILadder {
	percents: number
}

interface IUsePodium {
	podium: IPodium[],
	is_fetched: boolean
}

export const usePodiumPerMaxPoints = (ladder_type: EnumTriviaLadders): IUsePodium => {

	const { is_fetched } = useTableHook(ladder_type);

	const podium = useSelector(getPodiumSelector)(ladder_type);
	const [ firstPlace, secondPlace, thirdPlace ] = podium;

	const maxValue = maxBy([ firstPlace, secondPlace, thirdPlace ], 'max_contest_points');

	const mapPodium = (place: ILadder) => {
		const max_points = get(maxValue, 'max_contest_points', 1);
		const percents = place?.max_contest_points * 100 / max_points;

		return {
			...place,
			points  : place.max_contest_points || 0,
			percents: isNaN(percents) ? 0 : percents
		};
	};

	return {
		podium: map(podium, mapPodium),
		is_fetched: Boolean(is_fetched)
	};
};

export const usePodiumPerPoints = (ladder_type: EnumTriviaLadders): IUsePodium => {
	const { is_fetched } = useTableHook(ladder_type);

	const podium = useSelector(getPodiumSelector)(ladder_type);
	const [ firstPlace, secondPlace, thirdPlace ] = podium;

	const maxValue = maxBy([ firstPlace, secondPlace, thirdPlace ], 'points');

	const mapPodium = (place: ILadder) => {
		const max_points = get(maxValue, 'points', 1);
		const percents = place?.points * 100 / max_points;

		return {
			...place,
			points  : place.points || 0,
			percents: isNaN(percents) ? 0 : percents
		};
	};

	return {
		podium: map(podium, mapPodium),
		is_fetched: Boolean(is_fetched)
	};
};

export const usePodium = (ladder_type: EnumTriviaLadders) => {
	return {
		[EnumTriviaLadders.contest]           : usePodiumPerPoints,
		[ EnumTriviaLadders.contest_by_state ]: usePodiumPerPoints,
		[ EnumTriviaLadders.contest_by_store ]: usePodiumPerPoints,
		[ EnumTriviaLadders.month ]: usePodiumPerMaxPoints,
		[ EnumTriviaLadders.month_by_state ]: usePodiumPerMaxPoints,
		[ EnumTriviaLadders.month_by_store ]: usePodiumPerMaxPoints,
		[ EnumTriviaLadders.round ]: usePodiumPerPoints,
		[ EnumTriviaLadders.round_by_store ]: usePodiumPerPoints,
		[ EnumTriviaLadders.round_by_state ]: usePodiumPerPoints,
		[ EnumTriviaLadders.overall ]: usePodiumPerPoints,
		[ EnumTriviaLadders.overall_by_store ]: usePodiumPerPoints,
		[ EnumTriviaLadders.overall_by_state ]: usePodiumPerPoints,

		[ EnumTriviaLadders.team_season ]: usePodiumPerPoints,
		[ EnumTriviaLadders.team_season_state ]: usePodiumPerPoints,
		[ EnumTriviaLadders.team_season_store ]: usePodiumPerPoints,
		[ EnumTriviaLadders.team_month ]: usePodiumPerMaxPoints,
		[ EnumTriviaLadders.team_month_by_state ]: usePodiumPerMaxPoints,
		[ EnumTriviaLadders.team_month_by_store ]: usePodiumPerMaxPoints,
		[ EnumTriviaLadders.team_contest ]: usePodiumPerPoints,
		[ EnumTriviaLadders.team_contest_by_state ]: usePodiumPerPoints,
		[ EnumTriviaLadders.team_contest_by_store ]: usePodiumPerPoints,
		[ EnumTriviaLadders.team_round ]: usePodiumPerPoints,
		[ EnumTriviaLadders.team_round_by_store ]: usePodiumPerPoints,
		[ EnumTriviaLadders.team_round_by_state ]: usePodiumPerPoints,

	}[ ladder_type ](ladder_type)
};

export const useLadderAnimationClasses = () => {
	const contentRef = useRef<HTMLDivElement>(null);
	const tableRef = useRef<HTMLDivElement>(null);
	const manageClasses = () => {

		const current_table = tableRef.current;
		const current_content = contentRef.current;
		const current_row = current_table?.getElementsByTagName('div')[ 0 ];

		if ( current_table && current_content && current_row ) {

			if ( current_row.offsetHeight / 2 < current_table.scrollTop ) {
				current_content.classList.add('top_shadow');
			} else {
				current_content.classList.remove('top_shadow');
			}

			const is_bottom_shadow = current_table.scrollTop < ( current_table.scrollHeight - current_table.offsetHeight ) - current_row.offsetHeight;

			if ( is_bottom_shadow ) {
				current_content.classList.add('bottom_shadow');
			} else {
				current_content.classList.remove('bottom_shadow');
			}
		}
	};

	useEffect(() => {
		const timeout = setTimeout(manageClasses, 500);
		return () => {
			clearTimeout(timeout);
		};
	}, []);

	return {
		contentRef,
		tableRef,
		manageClasses
	};
};


const defaultLadderFilters = {
	direction: EnumOrders.Asc,
	limit: 25,
	offset: 0,
	month: ''
}

export const useFilters = () => {
	const [filters, setFilters] = useState(defaultLadderFilters);

	const changeOrder = () => {
		setFilters({
			...filters,
			direction: EnumOrders.Asc === filters.direction ? EnumOrders.Desc : EnumOrders.Asc
		})
	}

	const loadMore = () => {
		setFilters({
			...filters,
			limit: filters.limit + filters.limit
		})
	}

	const resetFilters = () => {
		setFilters({
			...defaultLadderFilters
		})
	}

	const setMonthStartDate = useCallback((month: string) => {
		setFilters((values) => ( {
			...values,
			month
		} ))
	}, [])

	return {
		filters,
		changeOrder,
		loadMore,
		setMonthStartDate,
		resetFilters
	}
}